import { push } from 'svelte-spa-router';

/**
 * @typedef {{ icon: string; text: string; url: string | (() => void) }} DropdownItem
 */

/**
 * @type {{
 *   dropdown: HTMLDivElement | null;
 *   icon: HTMLElement | null;
 *   iconId: string | null;
 *   dropdownId: string | null;
 *   clickHandler: ((event: MouseEvent) => void) | null;
 *   init: (iconId: string, items: DropdownItem[]) => void;
 *   positionDropdown: () => void;
 *   show: () => void;
 *   hide: () => void;
 *   toggle: () => void;
 *   remove: () => void;
 * }}
 */
export const dropdown = {
    dropdown: null,
    icon: null,
    iconId: null,
    dropdownId: null,
    clickHandler: null,

    /**
     * Initialize the dropdown.
     * @param {string} iconId - The id of the icon element.
     * @param {DropdownItem[]} items - The items to be displayed in the dropdown.
     */
    init(iconId, items) {
        this.icon = document.getElementById(iconId);
        if (!this.icon) {
            return;
        }

        this.remove();
        this.iconId = iconId;
        this.dropdownId = iconId + "-dropdown";

        // Create the dropdown element
        const dropdownEl = document.createElement('div');
        this.dropdown = dropdownEl;
        dropdownEl.id = this.dropdownId;
        dropdownEl.className = 'ui dropdown item dropdown-content';
        dropdownEl.style.display = 'none'; // Initially hide the dropdown
        dropdownEl.style.position = 'absolute'; // Position the dropdown absolutely

        // Append to the body
        document.body.appendChild(dropdownEl);

        // Position the dropdown below the icon
        const iconRect = this.icon.getBoundingClientRect();
        dropdownEl.style.left = iconRect.left + 'px';
        dropdownEl.style.top = iconRect.bottom + 'px';

        items.forEach(item => {
            const a = document.createElement('a');
            a.className = 'item';
            if (item.icon) {
                const iconEl = document.createElement('i');
                iconEl.className = item.icon;
                iconEl.setAttribute('aria-hidden', 'true');
                a.appendChild(iconEl);
                a.appendChild(document.createTextNode(' '));
            }
            a.appendChild(document.createTextNode(item.text));
            a.href = typeof item.url === 'string' ? item.url : '#';
            a.addEventListener('click', (event) => {
                this.hide();
                event.preventDefault();
                if (typeof item.url === 'string' && item.url.startsWith('/')) {
                    push(item.url);
                } else if (typeof item.url === 'function') {
                    item.url();
                } else {
                    console.error(`Invalid item.url type: ${typeof item.url}`);
                }
            });
            dropdownEl.appendChild(a);
        });

        this.clickHandler = (event) => {
            const icon = document.getElementById(this.iconId ?? '');
            const isClickInsideList = dropdownEl.contains(/** @type {Node} */ (event.target));
            let isClickInsideIcon = false;
            if (icon) {
                isClickInsideIcon = icon.contains(/** @type {Node} */ (event.target));
            }

            if (!isClickInsideList && !isClickInsideIcon) {
                this.hide();
            }
        };

        document.addEventListener('click', this.clickHandler);
    },

    /**
     * Position the dropdown.
     * Adjust the dropdown's position if it overflows the window's width.
     */
    positionDropdown() {
        if (!this.dropdown) return;
        const dropdownRect = this.dropdown.getBoundingClientRect();
        if (dropdownRect.left + dropdownRect.width > window.innerWidth) {
            this.dropdown.style.left = window.innerWidth - dropdownRect.width + 'px';
        }
    },

    /**
     * Show the dropdown.
     */
    show() {
        if (!this.dropdown) return;
        this.dropdown.style.display = 'block';
        this.positionDropdown();
    },

    /**
     * Hide the dropdown.
     */
    hide() {
        if (!this.dropdown) return;
        this.dropdown.style.display = 'none';
    },

    /**
     * Toggle the visibility of the dropdown.
     */
    toggle() {
        if (!this.dropdown) return;
        if (this.dropdown.style.display === 'none') {
            this.show();
        } else {
            this.hide();
        }
    },

    /**
     * Remove the dropdown.
     */
    remove() {
        if (this.dropdown) {
            while (this.dropdown.firstChild) {
                this.dropdown.removeChild(this.dropdown.firstChild);
            }
            document.body.removeChild(this.dropdown);
            if (this.clickHandler) {
                document.removeEventListener('click', this.clickHandler);
            }
            this.dropdown = null;
            this.clickHandler = null;
        }
    }
};
